var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import _ from 'lodash';
import { mapResponseToJson, mapResponseToText, UnsucessfulFetchError } from '@atlassiansox/asc-core';
import { generateAsapHeader } from '@atlassiansox/asc-asap';
import { fetch, Headers } from 'cross-fetch';
import { CHECK_ADD_PROJECT_ACTION, CHECK_ADD_PROJECT_ARI_PREFIX, isAsapAuth } from './identity-client.types';
export const DEFAULT_IDENTITY_BASE_URL = 'https://id.staging.atl-paas.net';
const ASAP_AUDIENCE = 'identity-platform';
export class IdentityClient {
    constructor({ baseUrlIdentity = DEFAULT_IDENTITY_BASE_URL } = {}) {
        this.baseUrlIdentity = baseUrlIdentity;
    }
    permissionsPermitted(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { auth, permissionId, principalId, resourceId, dontRequirePrincipalInSite } = request;
            // URL is defined globally for node versions >= 10.0.0 and globally in browsers,
            // so I'm assuming this should work cross platform
            const url = new URL(`${this.baseUrlIdentity}/v1/permissions/permitted`);
            const params = {
                permissionId,
                principalId,
                resourceId,
                dontRequirePrincipalInSite
            };
            _.map(params, (val, key) => {
                url.searchParams.append(key, val.toString());
            });
            const headers = new Headers();
            if (isAsapAuth(auth)) {
                headers.append('Authorization', generateAsapHeader(Object.assign(Object.assign({}, auth.asap), { audience: ASAP_AUDIENCE })));
            }
            return mapResponseToText(yield fetch(`${url}`, {
                method: 'GET',
                headers
            }));
        });
    }
    permissionsBulkPermitted(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { auth, query } = request;
            const url = new URL(`${this.baseUrlIdentity}/v3/permissions/bulk/permitted`);
            const headers = new Headers();
            if (isAsapAuth(auth)) {
                headers.append('Authorization', generateAsapHeader(Object.assign(Object.assign({}, auth.asap), { audience: ASAP_AUDIENCE })));
            }
            headers.append('Accept', 'application/json');
            headers.append('Content-Type', 'application/json');
            return mapResponseToText(yield fetch(`${url}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(query)
            }));
        });
    }
    checkAddProductCapabilities(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { auth, cloudId, product, licenceLimit } = request;
            const url = new URL(`${this.baseUrlIdentity}/v2/licences/capabilities`);
            const requestData = [
                {
                    action: CHECK_ADD_PROJECT_ACTION,
                    owner: `${CHECK_ADD_PROJECT_ARI_PREFIX}${cloudId}`,
                    product,
                    licenceLimit
                }
            ];
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');
            if (isAsapAuth(auth)) {
                headers.append('Authorization', generateAsapHeader(Object.assign(Object.assign({}, auth.asap), { audience: ASAP_AUDIENCE })));
            }
            const response = yield fetch(`${url}`, {
                method: 'post',
                headers,
                body: JSON.stringify(requestData)
            });
            if (response.ok) {
                return response.json();
            }
            else {
                throw new UnsucessfulFetchError({
                    response
                });
            }
        });
    }
    checkAuthenticationMethodsAvailable(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { email, redirect_to, application, auth } = options;
            return mapResponseToJson(yield fetch(`${this.baseUrlIdentity}/v2/authentication/methods-available`, {
                method: 'POST',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    redirect_to,
                    application
                })
            }));
        });
    }
    getUser(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield fetch(`${this.baseUrlIdentity}/v2/users/${accountId}`);
            if (result.status === 404) {
                return undefined;
            }
            if (!result.ok) {
                throw new Error(yield result.text());
            }
            return (yield result.json());
        });
    }
    getUserName(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.getUser(accountId);
            if (!user) {
                return undefined;
            }
            return user.name;
        });
    }
    getHealthCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            return mapResponseToText(yield fetch(`${this.baseUrlIdentity}/healthcheck`));
        });
    }
    userSignup(request, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { application, displayName, email, locale, recaptchaScore, redirectTo, suppressEmail, userAgent, anonymousId } = request;
            const headers = new Headers({
                'Authorization': generateAsapHeader(Object.assign(Object.assign({}, options.auth.asap), { audience: ASAP_AUDIENCE })),
                'Content-Type': 'application/json'
            });
            if (anonymousId) {
                headers.append('X-Atl-Anonymous-Id', anonymousId);
            }
            const context = Object.assign(Object.assign({}, (userAgent && {
                deviceInfo: {
                    userAgent
                }
            })), (recaptchaScore && {
                riskScore: {
                    type: 'reCAPTCHA',
                    value: recaptchaScore
                }
            }));
            const body = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ application }, (displayName && { displayName })), { email }), (locale && { locale })), (suppressEmail !== undefined && { suppressEmail })), (redirectTo && { redirect_to: redirectTo })), (Object.keys(context).length && { context }));
            return mapResponseToJson(yield fetch(`${this.baseUrlIdentity}/v2/users/signup`, {
                method: 'POST',
                headers,
                body: JSON.stringify(Object.assign(Object.assign({}, body), { redirect_to: redirectTo }))
            }));
        });
    }
    getUsersByCloudId(request) {
        return __awaiter(this, void 0, void 0, function* () {
            return mapResponseToJson(yield fetch(`${this.baseUrlIdentity}/v1/site/${request.cloudId}/Users`, {
                method: 'GET',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, request.auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                }
            }));
        });
    }
    getUserByCloudAndAaId(request) {
        return __awaiter(this, void 0, void 0, function* () {
            return mapResponseToJson(yield fetch(`${this.baseUrlIdentity}/v1/site/${request.cloudId}/Users/${request.atlassianAccountId}`, {
                method: 'GET',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, request.auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                }
            }));
        });
    }
    getPrincipalsByResource(request) {
        return __awaiter(this, void 0, void 0, function* () {
            return mapResponseToJson(yield fetch(`${this.baseUrlIdentity}/v3/permissions/principalsByResource`, {
                method: 'POST',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, request.auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    principalFilter: request.principalFilter,
                    resourceId: request.resourceId,
                    permissionId: request.permissionId
                })
            }));
        });
    }
    inviteUser(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { auth } = request, requestPayload = __rest(request, ["auth"]);
            const response = yield fetch(`${this.baseUrlIdentity}/v1/Users/invite`, {
                method: 'Post',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestPayload)
            });
            if (!response.ok) {
                throw new UnsucessfulFetchError({
                    message: `${response.statusText} ${yield response.text()}`,
                    response
                });
            }
            const invitationUrl = response.headers.get('location');
            if (!invitationUrl) {
                throw new Error('invitation request succeeded but no location header was returned');
            }
            return { invitationUrl };
        });
    }
    getUserIdsByEmailAddresses(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseUrlIdentity}/v2/users/fetch/by-email`, {
                method: 'POST',
                headers: {
                    'Authorization': generateAsapHeader(Object.assign(Object.assign({}, request.auth.asap), { audience: ASAP_AUDIENCE })),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    emails: request.emailAddresses
                })
            });
            if (!response.ok) {
                throw new Error(`${response.statusText} ${yield response.text()}`);
            }
            return response.json();
        });
    }
}
//# sourceMappingURL=identity-client.js.map